import { useState, useEffect, useRef } from "react";

// ─── 법령 기반 질문 데이터 ─────────────────────────────────────────────────────
const SECTIONS = [
  {
    id: "type",
    title: "현수막 유형 확인",
    subtitle: "공직선거법 제67조 · 제68조 · 옥외광고물법 제8조",
    icon: "📋",
    questions: [
      {
        id: "banner_type",
        text: "설치하려는 현수막의 종류는 무엇입니까?",
        law: "공직선거법 제67조, 옥외광고물법 제8조",
        type: "single",
        options: [
          { label: "후보자 선거운동 현수막", value: "candidate", score: 0, tag: "선관위 표지 필수" },
          { label: "정당 활동 현수막", value: "party", score: 0, tag: "개수·규격 제한 적용" },
          { label: "예비후보자 현수막", value: "pre_candidate", score: 0, tag: "선거사무소 건물에만 설치" },
          { label: "투표참여 권유 현수막", value: "vote_urge", score: 0, tag: "옥외광고물법 직접 적용" },
          { label: "일반 행사·홍보 현수막", value: "general", score: 0, tag: "옥외광고물법 전면 적용" },
        ],
      },
      {
        id: "election_period",
        text: "현재 선거운동 기간 중입니까?",
        law: "공직선거법 제59조(선거운동기간)",
        type: "single",
        options: [
          { label: "예, 공식 선거운동 기간 중", value: "yes", score: 10 },
          { label: "아니오, 선거운동 기간 전·후", value: "no", score: -20, flag: "⚠️ 선거운동기간 외 설치는 법령 위반 소지" },
        ],
        showIf: (answers) => ["candidate"].includes(answers.banner_type),
      },
      {
        id: "nec_sticker",
        text: "선거관리위원회 표지(스티커)를 현수막에 부착하였습니까?",
        law: "공직선거법 제67조 제3항",
        type: "single",
        options: [
          { label: "예, 선관위 표지 부착 완료", value: "yes", score: 10 },
          { label: "아니오, 미부착", value: "no", score: -30, flag: "❌ 선관위 표지 미부착은 법령 위반" },
          { label: "신청 예정 (아직 미부착)", value: "pending", score: -10, flag: "⚠️ 설치 전 반드시 선관위 표지 부착 필요" },
        ],
        showIf: (answers) => answers.banner_type === "candidate",
      },
    ],
  },
  {
    id: "location_absolute",
    title: "절대 금지 구역 확인",
    subtitle: "공직선거법 제68조 · 옥외광고물법 제4조 · 도로교통법",
    icon: "🚫",
    questions: [
      {
        id: "school_zone",
        text: "설치 위치가 어린이보호구역(스쿨존) 내부입니까?",
        law: "옥외광고물법 시행령(2024.1.12 개정), 공직선거관리규칙",
        type: "single",
        options: [
          { label: "예, 어린이보호구역 내부", value: "yes", score: -100, flag: "❌ 어린이보호구역 내 설치 절대 금지" },
          { label: "아니오, 구역 외부", value: "no", score: 10 },
          { label: "불확실 (확인 필요)", value: "unknown", score: -20, flag: "⚠️ 설치 전 반드시 관할 지자체 확인 필요" },
        ],
      },
      {
        id: "fire_facility",
        text: "설치 위치가 소방시설(소화전·소방용 방수구·비상소화장치 등) 주변입니까?",
        law: "소방기본법 · 옥외광고물법(2024.1.12 개정)",
        type: "single",
        options: [
          { label: "예, 소방시설 주변", value: "yes", score: -100, flag: "❌ 소방시설 주변 설치 절대 금지" },
          { label: "아니오, 소방시설 없음", value: "no", score: 10 },
        ],
      },
      {
        id: "cultural_heritage",
        text: "설치 위치가 문화재 보호구역 또는 그 주변 경관지구 내입니까?",
        law: "문화재보호법 제35조",
        type: "single",
        options: [
          { label: "예, 문화재 보호구역 내", value: "yes", score: -100, flag: "❌ 문화재 보호구역 내 설치 금지" },
          { label: "아니오, 해당 없음", value: "no", score: 10 },
        ],
      },
      {
        id: "traffic_facility",
        text: "설치 위치가 교통안전시설(신호등·도로표지판·가로등)에 부착 또는 가리는 위치입니까?",
        law: "도로교통법 제72조",
        type: "single",
        options: [
          { label: "예, 교통안전시설에 부착·가림", value: "yes", score: -100, flag: "❌ 교통안전시설 가림·부착 절대 금지" },
          { label: "아니오, 해당 없음", value: "no", score: 10 },
        ],
      },
    ],
  },
  {
    id: "location_conditional",
    title: "조건부 설치 구역 확인",
    subtitle: "도로교통법 · 옥외광고물법 시행령 · 선거광고물 관리지침",
    icon: "⚠️",
    questions: [
      {
        id: "intersection",
        text: "설치 위치가 교차로 가장자리 또는 도로 모퉁이로부터 5m 이내입니까?",
        law: "도로교통법 시행규칙, 옥외광고물법 시행령",
        type: "single",
        options: [
          { label: "예, 5m 이내", value: "yes", score: -20, flag: "⚠️ 현수막 하단 지면 2.5m 이상, 끈 높이 2m 이상 필수" },
          { label: "아니오, 5m 초과", value: "no", score: 10 },
          { label: "불확실", value: "unknown", score: -10 },
        ],
      },
      {
        id: "crosswalk_busstop",
        text: "설치 위치가 횡단보도 또는 버스정류장으로부터 10m 이내입니까?",
        law: "도로교통법 시행규칙, 선거광고물 관리지침(2026)",
        type: "single",
        options: [
          { label: "예, 10m 이내", value: "yes", score: -20, flag: "⚠️ 현수막 하단 지면 2.5m 이상, 끈 높이 2m 이상 필수" },
          { label: "아니오, 10m 초과", value: "no", score: 10 },
          { label: "불확실", value: "unknown", score: -10 },
        ],
      },
      {
        id: "height_near_road",
        text: "교차로·횡단보도 10m 이내인 경우, 현수막 하단 높이는 지면으로부터 2.5m 이상입니까?",
        law: "옥외광고물법 시행령, 선거광고물 관리지침",
        type: "single",
        showIf: (a) => a.intersection === "yes" || a.crosswalk_busstop === "yes",
        options: [
          { label: "예, 2.5m 이상 확보", value: "yes", score: 10 },
          { label: "아니오, 2.5m 미만", value: "no", score: -30, flag: "❌ 교통안전 위해 — 즉시 시정명령·철거 대상" },
          { label: "끈 높이만 2m 이상 확보", value: "rope_only", score: -10, flag: "⚠️ 본체 하단도 2.5m 이상 필요" },
        ],
      },
    ],
  },
  {
    id: "quantity",
    title: "수량 기준 확인",
    subtitle: "공직선거법 제67조 · 옥외광고물법 개정(2024.1.12)",
    icon: "🔢",
    questions: [
      {
        id: "emd_count",
        text: "해당 읍·면·동에 이미 설치된 현수막(동일 후보/정당) 수는?",
        law: "공직선거법 제67조: 읍·면·동별 2매 이내 / 면적 100㎢ 이상 시 +1매",
        type: "single",
        options: [
          { label: "0개 (최초 설치)", value: "0", score: 20 },
          { label: "1개 (1개 기설치)", value: "1", score: 10 },
          { label: "2개 이상 (한도 소진)", value: "2+", score: -30, flag: "❌ 읍·면·동별 설치 한도 초과 — 추가 설치 불가" },
          { label: "해당 읍·면·동 면적 100㎢ 이상 (1개 추가 허용)", value: "100km", score: 10 },
        ],
        showIf: (a) => ["candidate", "party", "pre_candidate"].includes(a.banner_type),
      },
      {
        id: "total_district_count",
        text: "해당 선거구 전체에서 설치 예정 수량은 선거구 내 읍·면·동 수의 2배 이내입니까?",
        law: "공직선거법 제67조 제1항: 선거구 내 읍·면·동 수의 2배 이내",
        type: "single",
        showIf: (a) => a.banner_type === "candidate",
        options: [
          { label: "예, 2배 이내", value: "yes", score: 10 },
          { label: "아니오, 2배 초과", value: "no", score: -30, flag: "❌ 전체 선거구 수량 한도 초과" },
          { label: "미계산 (확인 필요)", value: "unknown", score: -5 },
        ],
      },
    ],
  },
  {
    id: "spec",
    title: "규격 및 재질 기준",
    subtitle: "공직선거관리규칙 제32조 · 옥외광고물법 시행령",
    icon: "📐",
    questions: [
      {
        id: "size",
        text: "현수막의 면적은 10㎡ 이하입니까?",
        law: "공직선거관리규칙 제32조: 천 재질, 10㎡ 이내",
        type: "single",
        options: [
          { label: "예, 10㎡ 이하 (예: 2m×5m)", value: "yes", score: 10 },
          { label: "아니오, 10㎡ 초과", value: "no", score: -20, flag: "❌ 규정 초과 규격 — 선관위 승인 불가" },
          { label: "미측정 (확인 필요)", value: "unknown", score: -5 },
        ],
      },
      {
        id: "material",
        text: "현수막 재질이 천(직물)입니까?",
        law: "공직선거관리규칙 제32조: 천 재질 의무",
        type: "single",
        options: [
          { label: "예, 천(직물) 소재", value: "yes", score: 10 },
          { label: "아니오, 비닐·플라스틱 등 기타 재질", value: "no", score: -15, flag: "⚠️ 선거 현수막은 천 소재 사용 의무" },
        ],
        showIf: (a) => ["candidate", "party"].includes(a.banner_type),
      },
      {
        id: "blocking",
        text: "현수막이 출입문, 창문, 환기구, 비상구를 가리거나 막습니까?",
        law: "옥외광고물법 시행령 제18조",
        type: "single",
        options: [
          { label: "아니오, 출입·환기 방해 없음", value: "no", score: 10 },
          { label: "예, 일부 가림 발생", value: "yes", score: -25, flag: "❌ 출입문·환기구·비상구 가림 금지" },
        ],
      },
      {
        id: "safety_risk",
        text: "현수막이 낙하·추락·전도될 위험이 있는 방식으로 설치됩니까?",
        law: "선거광고물 관리지침(2026), 옥외광고물법",
        type: "single",
        options: [
          { label: "아니오, 안전하게 고정", value: "no", score: 10 },
          { label: "예, 낙하·추락 위험 있음", value: "yes", score: -40, flag: "❌ 안전 위해 — 즉시 시정명령 대상" },
          { label: "불확실 (고정 방법 미정)", value: "unknown", score: -10 },
        ],
      },
    ],
  },
  {
    id: "permit",
    title: "허가·신고 및 기타 요건",
    subtitle: "옥외광고물법 제3조 · 도로법 · 지자체 조례",
    icon: "📜",
    questions: [
      {
        id: "road_permit",
        text: "현수막을 도로 상에 설치하는 경우, 관할 지자체의 도로점용 허가를 받았습니까?",
        law: "도로법 제61조, 지자체 조례",
        type: "single",
        showIf: (a) => a.banner_type === "general" || a.banner_type === "vote_urge",
        options: [
          { label: "예, 도로점용 허가 완료", value: "yes", score: 10 },
          { label: "아니오, 허가 미취득", value: "no", score: -25, flag: "❌ 도로 설치 시 도로점용 허가 필수" },
          { label: "도로 상 설치 아님", value: "na", score: 5 },
        ],
      },
      {
        id: "local_report",
        text: "일반 현수막의 경우, 관할 지자체에 신고하였습니까?",
        law: "옥외광고물법 제3조(신고 의무)",
        type: "single",
        showIf: (a) => ["general", "vote_urge"].includes(a.banner_type),
        options: [
          { label: "예, 신고 완료", value: "yes", score: 10 },
          { label: "아니오, 신고 미완료", value: "no", score: -20, flag: "⚠️ 신고 후 설치 필요 (옥외광고물법 제3조)" },
          { label: "공직선거법 적용 대상 (신고 면제)", value: "exempt", score: 10 },
        ],
      },
      {
        id: "private_land",
        text: "타인 소유 토지·건물에 설치하는 경우, 소유자(점유자)의 동의를 받았습니까?",
        law: "민법 제214조(소유권에 기한 방해배제)",
        type: "single",
        options: [
          { label: "예, 동의 완료 (또는 자기 소유)", value: "yes", score: 10 },
          { label: "아니오, 동의 미취득", value: "no", score: -20, flag: "⚠️ 토지·건물주 동의 없는 설치는 분쟁 소지" },
          { label: "공유지·도로변 (동의 불필요)", value: "public", score: 5 },
        ],
      },
    ],
  },
];

// ─── 결과 계산 로직 ────────────────────────────────────────────────────────────
function calcResult(answers) {
  let score = 100;
  const flags = [];
  const hardFails = [];

  SECTIONS.forEach((sec) => {
    sec.questions.forEach((q) => {
      if (q.showIf && !q.showIf(answers)) return;
      const selected = answers[q.id];
      if (!selected) return;
      const opt = q.options.find((o) => o.value === selected);
      if (!opt) return;
      score += opt.score || 0;
      if (opt.flag) {
        if (opt.score <= -100 || opt.score <= -30) {
          hardFails.push({ text: opt.flag, law: q.law, q: q.text });
        } else {
          flags.push({ text: opt.flag, law: q.law, q: q.text });
        }
      }
    });
  });

  const absoluteFail = hardFails.some((f) => f.text.startsWith("❌") && f.q.includes("어린이") || f.q.includes("소방") || f.q.includes("문화재") || f.q.includes("교통안전시설") || f.q.includes("한도 초과"));

  let verdict, color, emoji, grade;
  if (hardFails.filter(f => f.text.startsWith("❌")).length > 0) {
    verdict = "설치 불가";
    color = "#dc2626";
    emoji = "❌";
    grade = "F";
  } else if (score >= 80 && flags.length === 0) {
    verdict = "설치 가능";
    color = "#16a34a";
    emoji = "✅";
    grade = "A";
  } else if (score >= 60 || (score >= 50 && flags.length <= 2)) {
    verdict = "조건부 설치 가능";
    color = "#d97706";
    emoji = "⚠️";
    grade = "B";
  } else {
    verdict = "설치 불가 (요건 미충족)";
    color = "#dc2626";
    emoji = "❌";
    grade = "F";
  }

  return { score: Math.max(0, Math.min(100, score)), verdict, color, emoji, grade, flags, hardFails };
}

// ─── 컴포넌트 ──────────────────────────────────────────────────────────────────
export default function App() {
  const [answers, setAnswers] = useState({});
  const [currentSection, setCurrentSection] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [animIn, setAnimIn] = useState(true);
  const topRef = useRef(null);

  const visibleSections = SECTIONS.map((sec) => ({
    ...sec,
    questions: sec.questions.filter((q) => !q.showIf || q.showIf(answers)),
  }));

  const currentQ = visibleSections[currentSection];
  const totalSections = visibleSections.length;
  const progress = ((currentSection) / totalSections) * 100;

  const allAnswered = currentQ?.questions.every((q) => answers[q.id] !== undefined);

  const handleAnswer = (qid, value) => {
    setAnswers((prev) => ({ ...prev, [qid]: value }));
  };

  const goNext = () => {
    if (currentSection < totalSections - 1) {
      setAnimIn(false);
      setTimeout(() => {
        setCurrentSection((s) => s + 1);
        setAnimIn(true);
        topRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 200);
    } else {
      setSubmitted(true);
      topRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const goPrev = () => {
    if (currentSection > 0) {
      setAnimIn(false);
      setTimeout(() => {
        setCurrentSection((s) => s - 1);
        setAnimIn(true);
        topRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 200);
    }
  };

  const reset = () => {
    setAnswers({});
    setCurrentSection(0);
    setSubmitted(false);
    setAnimIn(true);
  };

  const result = submitted ? calcResult(answers) : null;

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #0f1923 0%, #1a2e40 50%, #0f2035 100%)",
      fontFamily: "'Noto Sans KR', 'Pretendard', sans-serif",
      padding: "0",
      position: "relative",
      overflow: "hidden",
    }}>
      {/* 배경 장식 */}
      <div style={{
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0, pointerEvents: "none", zIndex: 0,
        background: "radial-gradient(ellipse 80% 60% at 20% 10%, rgba(59,130,246,0.08) 0%, transparent 60%), radial-gradient(ellipse 60% 80% at 80% 80%, rgba(16,185,129,0.06) 0%, transparent 60%)",
      }} />

      <div ref={topRef} style={{ position: "relative", zIndex: 1, maxWidth: 720, margin: "0 auto", padding: "24px 16px 60px" }}>

        {/* 헤더 */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 10,
            background: "rgba(59,130,246,0.12)", border: "1px solid rgba(59,130,246,0.3)",
            borderRadius: 50, padding: "6px 18px", marginBottom: 16,
          }}>
            <span style={{ fontSize: 12, color: "#93c5fd", fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase" }}>
              BANNERSCAN
            </span>
          </div>
          <h1 style={{ color: "#f0f6ff", fontSize: 26, fontWeight: 800, margin: "0 0 8px", lineHeight: 1.3 }}>
            선거 현수막 설치 가능 여부<br />
            <span style={{ color: "#3b82f6" }}>법규 진단 질문지</span>
          </h1>
          <p style={{ color: "#94a3b8", fontSize: 13, margin: 0, lineHeight: 1.6 }}>
            공직선거법 · 옥외광고물법 · 도로교통법 · 선거광고물 관리지침(2026) 기반
          </p>
        </div>

        {!submitted ? (
          <>
            {/* 진행률 바 */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <span style={{ color: "#64748b", fontSize: 12 }}>
                  {visibleSections.map((s, i) => (
                    <span key={s.id} style={{
                      display: "inline-block", width: 8, height: 8, borderRadius: "50%",
                      background: i < currentSection ? "#3b82f6" : i === currentSection ? "#60a5fa" : "#1e3a5f",
                      margin: "0 3px", transition: "all 0.3s",
                      boxShadow: i === currentSection ? "0 0 8px #3b82f6" : "none",
                    }} />
                  ))}
                </span>
                <span style={{ color: "#64748b", fontSize: 12 }}>{currentSection + 1} / {totalSections} 단계</span>
              </div>
              <div style={{ height: 4, background: "#1e3a5f", borderRadius: 2, overflow: "hidden" }}>
                <div style={{
                  height: "100%", borderRadius: 2,
                  background: "linear-gradient(90deg, #3b82f6, #06b6d4)",
                  width: `${progress}%`, transition: "width 0.4s ease",
                }} />
              </div>
            </div>

            {/* 질문 카드 */}
            <div style={{
              opacity: animIn ? 1 : 0, transform: animIn ? "translateY(0)" : "translateY(12px)",
              transition: "all 0.25s ease",
            }}>
              {/* 섹션 헤더 */}
              <div style={{
                background: "rgba(30,58,95,0.6)", border: "1px solid rgba(59,130,246,0.2)",
                borderRadius: 16, padding: "20px 24px", marginBottom: 16,
                backdropFilter: "blur(8px)",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 28 }}>{currentQ.icon}</span>
                  <div>
                    <div style={{ color: "#f0f6ff", fontWeight: 700, fontSize: 17 }}>{currentQ.title}</div>
                    <div style={{ color: "#64748b", fontSize: 11, marginTop: 3 }}>{currentQ.subtitle}</div>
                  </div>
                </div>
              </div>

              {/* 질문들 */}
              {currentQ.questions.map((q, qi) => (
                <div key={q.id} style={{
                  background: "rgba(15,25,35,0.7)", border: "1px solid rgba(59,130,246,0.15)",
                  borderRadius: 16, padding: "22px 24px", marginBottom: 14,
                  backdropFilter: "blur(8px)",
                }}>
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ color: "#e2e8f0", fontWeight: 600, fontSize: 15, lineHeight: 1.5, marginBottom: 6 }}>
                      <span style={{
                        display: "inline-block", width: 22, height: 22, borderRadius: "50%",
                        background: "rgba(59,130,246,0.2)", color: "#60a5fa",
                        fontSize: 11, fontWeight: 700, textAlign: "center", lineHeight: "22px",
                        marginRight: 8, verticalAlign: "middle",
                      }}>Q</span>
                      {q.text}
                    </div>
                    <div style={{
                      display: "inline-block", background: "rgba(59,130,246,0.08)",
                      border: "1px solid rgba(59,130,246,0.2)", borderRadius: 6,
                      padding: "3px 10px", fontSize: 10, color: "#64748b",
                    }}>
                      📌 근거: {q.law}
                    </div>
                  </div>

                  {/* 선택지 */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {q.options.map((opt) => {
                      const selected = answers[q.id] === opt.value;
                      const isDanger = (opt.score || 0) <= -25;
                      const isWarn = (opt.score || 0) < 0 && (opt.score || 0) > -25;
                      const isGood = (opt.score || 0) > 0;
                      return (
                        <button
                          key={opt.value}
                          onClick={() => handleAnswer(q.id, opt.value)}
                          style={{
                            display: "flex", alignItems: "flex-start", gap: 12,
                            background: selected
                              ? isDanger ? "rgba(220,38,38,0.15)" : isWarn ? "rgba(217,119,6,0.15)" : "rgba(22,163,74,0.15)"
                              : "rgba(255,255,255,0.03)",
                            border: `1.5px solid ${selected
                              ? isDanger ? "#dc2626" : isWarn ? "#d97706" : "#16a34a"
                              : "rgba(100,116,139,0.2)"}`,
                            borderRadius: 10, padding: "12px 16px",
                            cursor: "pointer", textAlign: "left", transition: "all 0.2s",
                            color: selected ? "#f0f6ff" : "#94a3b8",
                          }}
                        >
                          {/* 라디오 */}
                          <div style={{
                            width: 18, height: 18, borderRadius: "50%", flexShrink: 0, marginTop: 1,
                            border: `2px solid ${selected
                              ? isDanger ? "#dc2626" : isWarn ? "#d97706" : "#16a34a"
                              : "#334155"}`,
                            background: selected ? (isDanger ? "#dc2626" : isWarn ? "#d97706" : "#16a34a") : "transparent",
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}>
                            {selected && <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff" }} />}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 14, fontWeight: selected ? 600 : 400, lineHeight: 1.4 }}>
                              {opt.label}
                            </div>
                            {opt.tag && (
                              <div style={{ fontSize: 11, color: "#64748b", marginTop: 3 }}>{opt.tag}</div>
                            )}
                            {selected && opt.flag && (
                              <div style={{
                                marginTop: 8, padding: "6px 10px", borderRadius: 6, fontSize: 12,
                                background: isDanger ? "rgba(220,38,38,0.15)" : "rgba(217,119,6,0.15)",
                                color: isDanger ? "#fca5a5" : "#fcd34d",
                                border: `1px solid ${isDanger ? "rgba(220,38,38,0.3)" : "rgba(217,119,6,0.3)"}`,
                              }}>
                                {opt.flag}
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* 네비게이션 */}
            <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
              {currentSection > 0 && (
                <button onClick={goPrev} style={{
                  flex: 1, padding: "14px", borderRadius: 12, border: "1px solid rgba(100,116,139,0.3)",
                  background: "rgba(255,255,255,0.04)", color: "#94a3b8",
                  fontSize: 14, fontWeight: 600, cursor: "pointer",
                }}>
                  ← 이전
                </button>
              )}
              <button
                onClick={goNext}
                disabled={!allAnswered}
                style={{
                  flex: 2, padding: "14px", borderRadius: 12,
                  background: allAnswered
                    ? "linear-gradient(135deg, #2563eb, #0891b2)"
                    : "rgba(30,58,95,0.5)",
                  border: "none", color: allAnswered ? "#fff" : "#475569",
                  fontSize: 15, fontWeight: 700, cursor: allAnswered ? "pointer" : "not-allowed",
                  transition: "all 0.2s",
                  boxShadow: allAnswered ? "0 4px 20px rgba(37,99,235,0.3)" : "none",
                }}
              >
                {currentSection < totalSections - 1 ? "다음 단계 →" : "📊 결과 확인"}
              </button>
            </div>
            <p style={{ textAlign: "center", color: "#334155", fontSize: 11, marginTop: 16 }}>
              ⚠️ 본 진단은 참고용입니다. 최종 확인은 관할 선거관리위원회에 문의하시기 바랍니다.
            </p>
          </>
        ) : (
          /* ─── 결과 화면 ─── */
          <ResultScreen result={result} answers={answers} onReset={reset} />
        )}
      </div>
    </div>
  );
}

function ResultScreen({ result, answers, onReset }) {
  const { score, verdict, color, emoji, grade, flags, hardFails } = result;
  const allFlags = [...hardFails, ...flags];

  const scoreColor = grade === "A" ? "#16a34a" : grade === "B" ? "#d97706" : "#dc2626";
  const scoreBg = grade === "A" ? "rgba(22,163,74,0.1)" : grade === "B" ? "rgba(217,119,6,0.1)" : "rgba(220,38,38,0.1)";

  // 카테고리별 요약
  const categoryResults = SECTIONS.map((sec) => {
    const qs = sec.questions.filter((q) => !q.showIf || q.showIf(answers));
    const issues = qs.filter((q) => {
      const a = answers[q.id];
      if (!a) return false;
      const opt = q.options.find((o) => o.value === a);
      return opt && (opt.score || 0) < 0;
    });
    return { ...sec, totalQ: qs.length, issues: issues.length };
  });

  return (
    <div style={{ animation: "fadeIn 0.5s ease" }}>
      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}} @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.7}}`}</style>

      {/* 최종 판정 카드 */}
      <div style={{
        background: `linear-gradient(135deg, ${scoreBg}, rgba(15,25,35,0.9))`,
        border: `2px solid ${color}`,
        borderRadius: 20, padding: "32px 28px", marginBottom: 20, textAlign: "center",
        boxShadow: `0 8px 40px ${color}30`,
      }}>
        <div style={{ fontSize: 52, marginBottom: 8 }}>{emoji}</div>
        <div style={{ color, fontSize: 28, fontWeight: 900, marginBottom: 6 }}>{verdict}</div>
        <div style={{ color: "#94a3b8", fontSize: 14, marginBottom: 20 }}>
          진단 점수: <span style={{ color, fontWeight: 700, fontSize: 18 }}>{score}점</span> / 100점
        </div>

        {/* 점수 바 */}
        <div style={{ height: 10, background: "rgba(255,255,255,0.08)", borderRadius: 5, overflow: "hidden", marginBottom: 16 }}>
          <div style={{
            height: "100%", borderRadius: 5, width: `${score}%`,
            background: `linear-gradient(90deg, ${color}, ${color}88)`,
            transition: "width 1s ease",
          }} />
        </div>

        <div style={{
          display: "inline-block", background: scoreBg, border: `1px solid ${color}50`,
          borderRadius: 12, padding: "10px 20px",
          color: "#94a3b8", fontSize: 13, lineHeight: 1.6,
        }}>
          {grade === "A" && "✅ 현재 확인된 기준에서 설치 요건을 충족합니다.\n설치 전 관할 선관위에 최종 확인을 권장합니다."}
          {grade === "B" && "⚠️ 일부 조건 충족이 필요합니다.\n아래 주의 사항을 확인하고 보완 후 설치하세요."}
          {grade === "F" && "❌ 하나 이상의 설치 금지 요건이 확인되었습니다.\n설치 시 법령 위반 및 과태료·철거 처분 대상이 됩니다."}
        </div>
      </div>

      {/* 카테고리별 요약 */}
      <div style={{
        background: "rgba(15,25,35,0.8)", border: "1px solid rgba(59,130,246,0.15)",
        borderRadius: 16, padding: "20px 24px", marginBottom: 16,
      }}>
        <h3 style={{ color: "#e2e8f0", fontSize: 15, fontWeight: 700, margin: "0 0 16px" }}>
          📊 항목별 진단 결과
        </h3>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {categoryResults.map((cat) => {
            const ok = cat.issues === 0 && cat.totalQ > 0;
            const skip = cat.totalQ === 0;
            return (
              <div key={cat.id} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "10px 14px", borderRadius: 10,
                background: skip ? "rgba(255,255,255,0.02)" : ok ? "rgba(22,163,74,0.08)" : "rgba(220,38,38,0.08)",
                border: `1px solid ${skip ? "rgba(100,116,139,0.15)" : ok ? "rgba(22,163,74,0.2)" : "rgba(220,38,38,0.2)"}`,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 18 }}>{cat.icon}</span>
                  <span style={{ color: skip ? "#475569" : "#cbd5e1", fontSize: 13, fontWeight: 500 }}>{cat.title}</span>
                </div>
                <div style={{
                  fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
                  background: skip ? "rgba(100,116,139,0.1)" : ok ? "rgba(22,163,74,0.15)" : "rgba(220,38,38,0.15)",
                  color: skip ? "#475569" : ok ? "#4ade80" : "#f87171",
                }}>
                  {skip ? "해당없음" : ok ? "✓ 이상없음" : `⚠ ${cat.issues}건 주의`}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 위반/주의 사항 */}
      {allFlags.length > 0 && (
        <div style={{
          background: "rgba(15,25,35,0.8)", border: "1px solid rgba(220,38,38,0.2)",
          borderRadius: 16, padding: "20px 24px", marginBottom: 16,
        }}>
          <h3 style={{ color: "#fca5a5", fontSize: 15, fontWeight: 700, margin: "0 0 16px" }}>
            🚨 위반·주의 사항 상세
          </h3>
          {allFlags.map((f, i) => {
            const isFail = f.text.startsWith("❌");
            return (
              <div key={i} style={{
                padding: "12px 16px", borderRadius: 10, marginBottom: 8,
                background: isFail ? "rgba(220,38,38,0.1)" : "rgba(217,119,6,0.1)",
                border: `1px solid ${isFail ? "rgba(220,38,38,0.3)" : "rgba(217,119,6,0.3)"}`,
              }}>
                <div style={{ color: isFail ? "#fca5a5" : "#fcd34d", fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
                  {f.text}
                </div>
                <div style={{ color: "#64748b", fontSize: 11 }}>📌 {f.law}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* 법령 요약 */}
      <div style={{
        background: "rgba(15,25,35,0.8)", border: "1px solid rgba(59,130,246,0.15)",
        borderRadius: 16, padding: "20px 24px", marginBottom: 20,
      }}>
        <h3 style={{ color: "#e2e8f0", fontSize: 15, fontWeight: 700, margin: "0 0 14px" }}>
          📋 적용 법령 요약
        </h3>
        {[
          { law: "공직선거법 제67조", desc: "현수막 수량(선거구 내 읍·면·동 수의 2배 이내), 게시기간(선거운동기간 중)" },
          { law: "공직선거관리규칙 제32조", desc: "규격 10㎡ 이내, 천 소재, 선관위 표지(스티커) 부착 의무" },
          { law: "옥외광고물법 개정(2024.1.12)", desc: "어린이보호구역·소방시설 주변 설치 금지, 읍·면·동별 2개 한도" },
          { law: "선거광고물 관리지침(2026)", desc: "교차로·횡단보도 10m 이내 시 현수막 하단 2.5m·끈 2m 이상, 안전위해 즉시 시정명령" },
          { law: "도로교통법 제72조", desc: "신호등·도로표지판 등 교통안전시설 가림·부착 금지" },
        ].map((item, i) => (
          <div key={i} style={{
            display: "flex", gap: 12, padding: "10px 0",
            borderBottom: i < 4 ? "1px solid rgba(59,130,246,0.08)" : "none",
          }}>
            <div style={{
              flexShrink: 0, background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.2)",
              borderRadius: 6, padding: "3px 8px", fontSize: 10, color: "#60a5fa", fontWeight: 600,
              height: "fit-content", whiteSpace: "nowrap",
            }}>
              {item.law}
            </div>
            <div style={{ color: "#94a3b8", fontSize: 12, lineHeight: 1.6 }}>{item.desc}</div>
          </div>
        ))}
      </div>

      {/* 다음 단계 안내 */}
      <div style={{
        background: "rgba(30,58,95,0.4)", border: "1px solid rgba(59,130,246,0.2)",
        borderRadius: 16, padding: "18px 24px", marginBottom: 20,
      }}>
        <h3 style={{ color: "#60a5fa", fontSize: 14, fontWeight: 700, margin: "0 0 12px" }}>
          📞 다음 단계 — 최종 확인
        </h3>
        <div style={{ color: "#94a3b8", fontSize: 13, lineHeight: 1.8 }}>
          • <strong style={{ color: "#cbd5e1" }}>중앙선거관리위원회</strong> 대표전화: 02-503-1114<br />
          • <strong style={{ color: "#cbd5e1" }}>관할 시·도 선거관리위원회</strong>: 설치 지역 확인 후 해당 선관위 문의<br />
          • <strong style={{ color: "#cbd5e1" }}>불법광고물 신고</strong>: 안전신문고 앱 또는 관할 지자체<br />
          • <strong style={{ color: "#cbd5e1" }}>법령 정보</strong>: 법제처 국가법령정보센터 law.go.kr
        </div>
      </div>

      {/* 버튼들 */}
      <div style={{ display: "flex", gap: 12 }}>
        <button onClick={onReset} style={{
          flex: 1, padding: "14px", borderRadius: 12,
          border: "1px solid rgba(59,130,246,0.3)",
          background: "rgba(59,130,246,0.08)", color: "#60a5fa",
          fontSize: 14, fontWeight: 700, cursor: "pointer",
        }}>
          🔄 다시 진단하기
        </button>
        <button
          onClick={() => window.print?.()}
          style={{
            flex: 1, padding: "14px", borderRadius: 12,
            background: "linear-gradient(135deg, #2563eb, #0891b2)",
            border: "none", color: "#fff",
            fontSize: 14, fontWeight: 700, cursor: "pointer",
          }}
        >
          🖨️ 결과 인쇄·저장
        </button>
      </div>

      <p style={{ textAlign: "center", color: "#334155", fontSize: 11, marginTop: 20, lineHeight: 1.7 }}>
        ※ 본 진단 결과는 일반적인 법령 기준에 의한 참고용 안내이며,<br />
        법적 효력을 갖지 않습니다. 최종 설치 가능 여부는 반드시<br />
        관할 선거관리위원회에 직접 문의하여 확인하시기 바랍니다.
      </p>
    </div>
  );
}